import classNames from 'classnames';
import { FunctionComponent, useState, useRef, useEffect } from 'react';

import { useWeblab } from '@amzn/sustainability-central-shared-react';

import { ModalBodyScroll } from 'src/components/common/modal-body-scroll/ModalBodyScroll';
import { ASX_REBRANDING, ASX_USER_LOCALE } from 'src/constants';
import { useDropdownContext } from 'src/contexts/DropdownContext';
import { THEME } from 'src/enums/theme';
import useDeviceSize from 'src/hooks/useDeviceSize';
import CheckIcon from 'src/icons/check.svg';
import ChevronDownRebrandingIcon from 'src/icons/chevron-down-rebranding.svg';
import ChevronDownIcon from 'src/icons/chevron-down.svg';
import { emitCountMetric } from 'src/logger';
import { LOCALE_PICKER_COUNT } from 'src/logging-helpers/metrics-constants';

import styles from './LocalePicker.module.scss';

const DEFAULT_LOCALE = 'en-US';
const MONS_LOCALE_QUERY_PARAM = '?mons_sel_locale=';

const LEGACY_LABELS_BY_LOCALE: Record<string, string> = {
  'ar-AE': 'العربية (AE)',
  'bn-IN': 'বাংলা (IN)',
  'cs-CZ': 'Čeština (CZ)',
  'de-DE': 'Deutsch (DE)',
  'en-US': 'English (US)',
  'es-ES': 'Español (ES)',
  'es-MX': 'Español (MX)',
  'fr-FR': 'Français (FR)',
  'hi-IN': 'हिन्दी (IN)',
  'it-IT': 'Italiano (IT)',
  'ja-JP': '日本語 (日本)',
  'km-KH': 'ខ្មែរ (KH)',
  'kn-IN': 'ಕನ್ನಡ (IN)',
  'mr-IN': 'मराठी (IN)',
  'pl-PL': 'Polski (PL)',
  'pt-BR': 'Português (BR)',
  'pt-PT': 'Português (PT)',
  'si-LK': 'සිංහල (LK)',
  'sk-SK': 'Slovenčina (SK)',
  'ta-IN': 'தமிழ் (IN)',
  'te-IN': 'తెలుగు (IN)',
  'tr-TR': 'Türkçe (TR)',
  'vi-VN': 'Tiếng Việt (VN)',
  'zh-CN': '普通话 (中国)',
  'zh-TW': '中文 (繁體)',
};

const REBRANDED_LABELS_BY_LOCALE: Record<string, { short: string; full: string; mobileLabel: string }> = {
  'ar-AE': {
    short: '🇦🇪 AR',
    full: '🇦🇪 العربية (AE)',
    mobileLabel: 'العربية (AE)',
  },
  'bn-IN': {
    short: '🇮🇳 BN',
    full: '🇮🇳 বাংলা (IN)',
    mobileLabel: 'বাংলা (IN)',
  },
  'cs-CZ': {
    short: '🇨🇿 CS',
    full: '🇨🇿 Čeština (CZ)',
    mobileLabel: 'Čeština (CZ)',
  },
  'de-DE': {
    short: '🇩🇪 DE',
    full: '🇩🇪 Deutsch (DE)',
    mobileLabel: 'Deutsch (DE)',
  },
  'en-US': {
    short: '🇺🇸 EN',
    full: '🇺🇸 English (US)',
    mobileLabel: 'English (US)',
  },
  'es-ES': {
    short: '🇪🇸 ES',
    full: '🇪🇸 Español (ES)',
    mobileLabel: 'Español (ES)',
  },
  'es-MX': {
    short: '🇲🇽 ES',
    full: '🇲🇽 Español (MX)',
    mobileLabel: 'Español (MX)',
  },
  'fr-FR': {
    short: '🇫🇷 FR',
    full: '🇫🇷 Français (FR)',
    mobileLabel: 'Français (FR)',
  },
  'hi-IN': {
    short: '🇮🇳 HI',
    full: '🇮🇳 हिन्दी (IN)',
    mobileLabel: 'हिन्दी (IN)',
  },
  'it-IT': {
    short: '🇮🇹 IT',
    full: '🇮🇹 Italiano (IT)',
    mobileLabel: 'Italiano (IT)',
  },
  'ja-JP': {
    short: '🇯🇵 JA',
    full: '🇯🇵 日本語 (日本)',
    mobileLabel: '日本語 (日本)',
  },
  'km-KH': {
    short: '🇰🇭 KM',
    full: '🇰🇭 ខ្មែរ (KH)',
    mobileLabel: 'ខ្មែរ (KH)',
  },
  'kn-IN': {
    short: '🇮🇳 KN',
    full: '🇮🇳 ಕನ್ನಡ (IN)',
    mobileLabel: 'ಕನ್ನಡ (IN)',
  },
  'mr-IN': {
    short: '🇮🇳 MR',
    full: '🇮🇳 मराठी (IN)',
    mobileLabel: 'मराठी (IN)',
  },
  'pl-PL': {
    short: '🇵🇱 PL',
    full: '🇵🇱 Polski (PL)',
    mobileLabel: 'Polski (PL)',
  },
  'pt-BR': {
    short: '🇧🇷 PT',
    full: '🇧🇷 Português (BR)',
    mobileLabel: 'Português (BR)',
  },
  'pt-PT': {
    short: '🇵🇹 PT',
    full: '🇵🇹 Português (PT)',
    mobileLabel: 'Português (PT)',
  },
  'si-LK': {
    short: '🇱🇰 SI',
    full: '🇱🇰 සිංහල (LK)',
    mobileLabel: 'සිංහල (LK)',
  },
  'sk-SK': {
    short: '🇸🇰 SK',
    full: '🇸🇰 Slovenčina (SK)',
    mobileLabel: 'Slovenčina (SK)',
  },
  'ta-IN': {
    short: '🇮🇳 TA',
    full: '🇮🇳 தமிழ் (IN)',
    mobileLabel: 'தமிழ் (IN)',
  },
  'te-IN': {
    short: '🇮🇳 TE',
    full: '🇮🇳 తెలుగు (IN)',
    mobileLabel: 'తెలుగు (IN)',
  },
  'tr-TR': {
    short: '🇹🇷 TR',
    full: '🇹🇷 Türkçe (TR)',
    mobileLabel: 'Türkçe (TR)',
  },
  'vi-VN': {
    short: '🇻🇳 VI',
    full: '🇻🇳 Tiếng Việt (VN)',
    mobileLabel: 'Tiếng Việt (VN)',
  },
  'zh-CN': {
    short: '🇨🇳 ZH',
    full: '🇨🇳 普通话 (中国)',
    mobileLabel: '普通话 (中国)',
  },
  'zh-TW': {
    short: '🇹🇼 ZH',
    full: '🇹🇼 中文 (繁體)',
    mobileLabel: '中文 (繁體)',
  },
};
interface LocaleOptionProps {
  label: string;
  isSelected: boolean;
  onClick: () => void;
}

/**
 * Component for individual locale option row in dropdown.
 */
const LocaleOption: FunctionComponent<LocaleOptionProps> = ({ label, isSelected, onClick }) => (
  <div
    className={classNames(styles.dropdownOption, { [styles.selected]: isSelected })}
    onClick={onClick}
    onKeyDown={(e) => e.key === 'Enter' && onClick()}
    role="option"
    aria-selected={isSelected}
    tabIndex={0}
  >
    {label}
    {isSelected && (
      <span className={styles.checkContainer}>
        <CheckIcon className={styles.checkIcon} />
      </span>
    )}
  </div>
);

type LocalePickerVariant = 'primary' | 'secondary';
interface LocalePickerProps {
  /** Variant describes the style of the locale picker when the sub-nav or mobile-nav menus are expanded. "primary" variant for collapsed and "secondary" for expanded. */
  variant: LocalePickerVariant;
  /** Theme describes the style of the locale picker against the main theme of the navbar */
  theme?: THEME;
  ariaLabel?: string;
  selectLanguageCopy: string;
}

/**
 * Component for locale picker button and dropdown.
 */
export const LocalePicker: FunctionComponent<LocalePickerProps> = ({
  variant = 'primary',
  theme = THEME.LIGHT,
  ariaLabel,
  selectLanguageCopy,
}) => {
  const { isSmallDesktop } = useDeviceSize();
  const { isDropdownOpen, toggleDropdown, closeDropdown, registerDropdownRef } = useDropdownContext();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [isScrollEnd, setIsScrollEnd] = useState(false);

  const [currLocale, setCurrLocale] = useState(localStorage.getItem(ASX_USER_LOCALE) ?? DEFAULT_LOCALE);

  const isExpanded = isDropdownOpen('locale-picker');

  const asxRebrandingWeblab = useWeblab(ASX_REBRANDING);

  const isRebrandingApplied =
    asxRebrandingWeblab === 'T1' || theme === THEME.REBRAND_DARK || theme === THEME.REBRAND_LIGHT;

  const shouldUseCustomMobilePicker = isRebrandingApplied || isSmallDesktop;

  const DownIcon = isRebrandingApplied ? ChevronDownRebrandingIcon : ChevronDownIcon;

  const localeLabels = isRebrandingApplied ? REBRANDED_LABELS_BY_LOCALE : LEGACY_LABELS_BY_LOCALE;
  const currentLocaleLabel = localeLabels[currLocale];

  // Register the dropdown ref for outside click detection
  useEffect(() => {
    registerDropdownRef('locale-picker', dropdownRef.current);
    return () => registerDropdownRef('locale-picker', null);
  }, [registerDropdownRef]);

  useEffect(() => {
    if (!isSmallDesktop && isExpanded) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [isExpanded, isSmallDesktop]);

  // Only apply the secondary (expanded) variant if the navbar is not dark themed. When dark themed, the collapsed and expanded variants are the same style.
  const isSecondary = variant === 'secondary' && theme !== THEME.DARK;

  /**
   * On locale change, set localStorage value on browser and redirect with Mons Selection API
   * https://w.amazon.com/bin/view/Mons/Selections/#HMonsLanguageAPI
   */
  const handleChange = (localeCode: string) => {
    setCurrLocale(localeCode);
    localStorage.setItem(ASX_USER_LOCALE, localeCode);
    closeDropdown();

    // emit EMF metric to determine which Locale users choose
    emitCountMetric(LOCALE_PICKER_COUNT, 1, { Locale: localeCode });

    // Convert locale string to Mons format and invoke redirect URL to set cookie on future requests.
    const monsLocale = localeCode.replace('-', '_');
    window.location.assign(`${window.location.pathname}${MONS_LOCALE_QUERY_PARAM}${monsLocale}`);
  };

  let localeDisplayLabel: string;

  if (typeof currentLocaleLabel === 'string') {
    localeDisplayLabel = currentLocaleLabel;
  } else if (isSmallDesktop) {
    localeDisplayLabel = currentLocaleLabel.short;
  } else {
    localeDisplayLabel = currentLocaleLabel.mobileLabel;
  }

  const desktopDropdown = (
    <div
      className={classNames(styles.dropdown, styles.show, styles[theme])}
      onMouseEnter={() => {}}
      onMouseLeave={closeDropdown}
    >
      {Object.entries(localeLabels).map(([localeCode, labels]) => (
        <LocaleOption
          key={localeCode}
          label={typeof labels === 'string' ? labels : labels.full}
          isSelected={currLocale === localeCode}
          onClick={() => handleChange(localeCode)}
        />
      ))}
    </div>
  );

  const mobileDropdownRebranding = (
    <dialog
      ref={dialogRef}
      className={classNames(styles.mobileDialog, styles[theme])}
      aria-label={selectLanguageCopy}
      onClose={closeDropdown}
    >
      <ModalBodyScroll
        className={styles.bodyScroll}
        onScrollEnd={
          // istanbul ignore next
          () => {
            setIsScrollEnd(true);
          }
        }
        onScrollAway={
          // istanbul ignore next
          () => {
            setIsScrollEnd(false);
          }
        }
      >
        <div className={styles.mobileDialogHeader}>
          <span>{selectLanguageCopy}</span>

          <button type="button" onClick={closeDropdown} className={styles.closeButton} aria-label="Close locale picker">
            ✕
          </button>
        </div>

        <div className={styles.mobileDialogContent}>
          {Object.entries(localeLabels).map(([localeCode, labels]) => (
            <LocaleOption
              key={localeCode}
              label={typeof labels === 'string' ? labels : labels.full}
              isSelected={currLocale === localeCode}
              onClick={() => handleChange(localeCode)}
            />
          ))}
        </div>
      </ModalBodyScroll>
      <div className={classNames(styles.gradient, { [styles.scrollEnd]: isScrollEnd })} />
    </dialog>
  );

  const mobileDropdown = (
    <div className={classNames(styles.mobileSelectWrapper, styles[theme])}>
      <select
        aria-label="locale-picker"
        className={classNames(styles.mobileSelect)}
        data-testid="mobile-locale-picker"
        onChange={(event) => handleChange(event.target.value)}
        value={currLocale}
      >
        {Object.entries(localeLabels).map(([localeCode, label]) => (
          <option key={localeCode} label={label} value={localeCode} onClick={() => handleChange(localeCode)} />
        ))}
      </select>
    </div>
  );

  const localePickerButton = (
    <div
      role="menu"
      tabIndex={0}
      aria-label={ariaLabel}
      className={classNames(styles.localePicker, styles[theme], {
        [styles.secondary]: isSecondary,
      })}
      onClick={() => {
        toggleDropdown('locale-picker');
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          toggleDropdown('locale-picker');
        }
      }}
    >
      <span className={styles.localeLabel}>{localeDisplayLabel}</span>

      <span>
        <DownIcon
          className={classNames(styles.localePickerIcon, {
            [styles.rotated]: isExpanded,
          })}
        />
      </span>
    </div>
  );

  if (!shouldUseCustomMobilePicker) {
    return mobileDropdown;
  }

  return (
    <div ref={dropdownRef}>
      {localePickerButton}

      {isExpanded && (isSmallDesktop ? desktopDropdown : mobileDropdownRebranding)}
    </div>
  );
};
